import React from 'react';

import {
  getCurrentFocusKey,
  setFocus,
  doesFocusableExist
} from '@noriginmedia/norigin-spatial-navigation';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';

export interface CollectionsContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface CollectionsContextValues {
  openCollections: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  collectionsFocusKey: React.MutableRefObject<string>;
}

export const CollectionsContext = React.createContext({} as CollectionsContextValues);

export const CollectionsProvider = (props: CollectionsContextProps) => {
  const { children } = props;
  const { player, playerRootElem } = usePlayerContext();

  const [openCollections, setOpenCollection] = React.useState(false);
  const collectionsFocusKey = React.useRef('');

  const handleOpen = React.useCallback(() => {
    collectionsFocusKey.current = getCurrentFocusKey();
    setOpenCollection(true);
  }, []);

  const handleClose = React.useCallback(() => {
    if (collectionsFocusKey.current) {
      // Check if the original focus key still exists
      if (doesFocusableExist(collectionsFocusKey.current)) {
        setFocus(collectionsFocusKey.current);
      } else {
        // If it's a showAll item that no longer exists, find the last available item in that stripe
        const showAllMatch = collectionsFocusKey.current.match(/^showAll-(\d+)$/);

        if (showAllMatch) {
          const stripeIndex = showAllMatch[1];

          // Try to find the last content item in this stripe
          for (let index = 100; index >= 0; index -= 1) {
            const candidate = `${index}-${stripeIndex}`;
            if (doesFocusableExist(candidate)) {
              setFocus(candidate);
              break;
            }
          }
        } else {
          // For other cases, try to find a previous item in the same stripe
          const stripeMatch = collectionsFocusKey.current.match(/^(\d+)-(\d+)$/);

          if (stripeMatch) {
            const currentIndex = Number(stripeMatch[1]);
            const stripeIndex = stripeMatch[2];

            for (let index = currentIndex - 1; index >= 0; index -= 1) {
              const candidate = `${index}-${stripeIndex}`;
              if (doesFocusableExist(candidate)) {
                setFocus(candidate);
                break;
              }
            }
          }
        }
      }
    }
    if (player) {
      playerRootElem?.focus();
    }
    setOpenCollection(false);
  }, [player, playerRootElem]);

  return (
    <CollectionsContext.Provider
      value={{
        openCollections,
        handleOpen,
        handleClose,
        collectionsFocusKey
      }}
    >
      {children}
    </CollectionsContext.Provider>
  );
};

export default CollectionsContext;
